import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Grid,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    IconButton,
    InputAdornment,
    Alert,
    Chip,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import {
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    AddCircle as AddCircleIcon,
    AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { StaticQuizDTO, QuizQuestionDTO, generateQuizQuestionsWithAI } from '../../services/quizService';

interface QuizEditorProps {
    topicId: number;
    existingQuiz?: StaticQuizDTO;
    onSave: (quiz: Partial<StaticQuizDTO>) => void;
    onCancel: () => void;
    /** When true, open the AI Generate dialog on mount (e.g. when user clicked "Create with AI") */
    openAIDialogOnMount?: boolean;
    /** Validation or save error from parent (e.g. backend "Quiz title is required") - shown on screen */
    saveError?: string | null;
    onClearSaveError?: () => void;
}

const QUIZ_AI_QUESTION_TYPES = [
    { value: 'MULTIPLE_CHOICE', label: 'Multiple Choice (MCQ)' },
    { value: 'TRUE_FALSE', label: 'True / False' },
    { value: 'SHORT_ANSWER', label: 'Short Answer' },
] as const;

const QuizEditor: React.FC<QuizEditorProps> = ({ topicId, existingQuiz, onSave, onCancel, openAIDialogOnMount, saveError, onClearSaveError }) => {
    const [title, setTitle] = useState(existingQuiz?.title || '');
    const [timeLimit, setTimeLimit] = useState(existingQuiz?.timeLimitMinutes || 20);
    const [passPercent, setPassPercent] = useState(existingQuiz?.minPassPercent || 70);
    const [status, setStatus] = useState<'DRAFT' | 'PUBLISHED' | 'ARCHIVED'>(existingQuiz?.status || 'DRAFT');
    const [questions, setQuestions] = useState<QuizQuestionDTO[]>(existingQuiz?.questions || []);
    
    // AI generation
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const [aiGenerating, setAiGenerating] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    const [aiQuestionCount, setAiQuestionCount] = useState(5);
    const [aiDifficulty, setAiDifficulty] = useState('MEDIUM');
    const [aiQuestionTypes, setAiQuestionTypes] = useState<string[]>(['MULTIPLE_CHOICE', 'TRUE_FALSE']);
    const [aiInstructions, setAiInstructions] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);
    
    useEffect(() => {
        if (openAIDialogOnMount && !existingQuiz) setAiDialogOpen(true);
    }, [openAIDialogOnMount, existingQuiz]);
    
    // Quiz-level question type - all questions must be the same type
    const [quizQuestionType, setQuizQuestionType] = useState<string>(() => {
        if (existingQuiz?.questions && existingQuiz.questions.length > 0) {
            return existingQuiz.questions[0].type;
        }
        return 'MCQ';
    });

    useEffect(() => {
        if (openAIDialogOnMount && !existingQuiz) {
            setAiDialogOpen(true);
        }
    }, [openAIDialogOnMount, existingQuiz]);

    const getDefaultQuestionForType = (type: string): QuizQuestionDTO => {
        const base = {
            id: `new-${Date.now()}-${Math.random()}`,
            type: type as any,
            content: 'New Question',
            points: 1
        };

        switch (type) {
            case 'MCQ':
                return { ...base, options: ['Option 1', 'Option 2', 'Option 3'], correctAnswer: 'Option 1' };
            case 'TRUE_FALSE':
                return { ...base, options: ['True', 'False'], correctAnswer: 'True' };
            case 'SHORT_ANSWER':
                return { ...base, correctAnswer: 'Correct answer' };
            case 'FILL_BLANK':
                return { ...base, content: 'Fill in the blank: ___ is the answer', correctAnswer: { blank1: 'answer' } };
            case 'NUMERIC':
                return { ...base, correctAnswer: 0, options: [{ tolerance: 0 }] };
            case 'MULTIPLE_SELECT':
                return { ...base, options: ['Option 1', 'Option 2', 'Option 3'], correctAnswer: ['Option 1', 'Option 2'] };
            case 'ORDERING':
                return { ...base, options: ['First', 'Second', 'Third'], correctAnswer: ['First', 'Second', 'Third'] };
            case 'MATCHING':
                return {
                    ...base,
                    options: {
                        leftItems: ['Item 1', 'Item 2'],
                        rightItems: ['Match 1', 'Match 2']
                    },
                    correctAnswer: { 'Item 1': 'Match 1', 'Item 2': 'Match 2' }
                };
            case 'DRAG_DROP':
                return {
                    ...base,
                    options: {
                        items: ['Drag Item 1', 'Drag Item 2'],
                        zones: ['Zone A', 'Zone B']
                    },
                    correctAnswer: { 'Drag Item 1': 'Zone A', 'Drag Item 2': 'Zone B' }
                };
            default:
                return { ...base, options: [], correctAnswer: '' };
        }
    };

    const handleAddQuestion = () => {
        const newQuestion = getDefaultQuestionForType(quizQuestionType);
        setQuestions([...questions, newQuestion]);
    };

    const handleGenerateWithAI = async () => {
        setAiGenerating(true);
        setAiError(null);
        try {
            const generated = await generateQuizQuestionsWithAI(topicId, {
                questionCount: aiQuestionCount,
                questionTypes: aiQuestionTypes.length ? aiQuestionTypes : ['MULTIPLE_CHOICE'],
                difficultyLevel: aiDifficulty,
                additionalInstructions: aiInstructions || undefined,
            });
            if (generated.length === 0) {
                setAiError('No questions were generated. Try different options or check the topic has content.');
                return;
            }
            setQuestions(generated);
            const firstType = generated[0].type;
            setQuizQuestionType(firstType);
            setAiDialogOpen(false);
        } catch (e: any) {
            setAiError(e?.response?.data?.message || e?.message || 'Failed to generate questions. Please try again.');
        } finally {
            setAiGenerating(false);
        }
    };

    const toggleAiQuestionType = (value: string) => {
        setAiQuestionTypes((prev) =>
            prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
        );
    };

    // When quiz question type changes, update all questions to match
    const handleQuizQuestionTypeChange = (newType: string) => {
        setQuizQuestionType(newType);
        if (questions.length > 0) {
            // Update all existing questions to the new type
            const updatedQuestions = questions.map(q => {
                const newQuestion = getDefaultQuestionForType(newType);
                return {
                    ...newQuestion,
                    id: q.id, // Keep existing IDs
                    content: q.content, // Keep existing content
                    points: q.points || 1, // Keep existing points
                    explanation: q.explanation // Keep existing explanation
                };
            });
            setQuestions(updatedQuestions);
        }
    };

    const handleUpdateQuestion = (index: number, field: keyof QuizQuestionDTO, value: any) => {
        const updated = [...questions];
        updated[index] = { ...updated[index], [field]: value };
        setQuestions(updated);
    };

    const handleDeleteQuestion = (index: number) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        // Validate before publishing
        setValidationError(null);
        if (status === 'PUBLISHED') {
            if (!title.trim()) {
                setValidationError('Quiz title is required before publishing.');
                return;
            }
            if (questions.length === 0) {
                setValidationError('Please add at least one question before publishing.');
                return;
            }
        }

        const quizData: Partial<StaticQuizDTO> = {
            id: existingQuiz?.id, // undefined for new
            topicId,
            title: title?.trim() || 'Untitled Quiz',
            timeLimitMinutes: timeLimit,
            minPassPercent: passPercent,
            questions,
            type: 'STATIC',
            status: status
        };
        // Log payload so you can share with support if save fails (especially AI-generated question shapes)
        console.log('[QuizEditor] Saving quiz payload:', {
            topicId: quizData.topicId,
            title: quizData.title,
            status: quizData.status,
            questionCount: quizData.questions?.length,
            questions: quizData.questions?.map((q, i) => ({
                index: i,
                type: q.type,
                content: (q.content || '').slice(0, 80),
                options: q.options,
                correctAnswer: q.correctAnswer,
                points: q.points,
            })),
        });
        onSave(quizData);
    };

    const displayError = saveError || validationError;

    return (
        <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>{existingQuiz ? 'Edit Quiz' : 'Create New Quiz'}</Typography>
            <Divider sx={{ mb: 3 }} />

            {displayError && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setValidationError(null);
                        onClearSaveError?.();
                    }}
                >
                    {displayError}
                </Alert>
            )}

            {/* Metadata Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Quiz Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel>Question Type</InputLabel>
                        <Select
                            value={quizQuestionType}
                            label="Question Type"
                            onChange={(e) => handleQuizQuestionTypeChange(e.target.value)}
                        >
                            <MenuItem value="MCQ">Multiple Choice (MCQ)</MenuItem>
                            <MenuItem value="TRUE_FALSE">True/False</MenuItem>
                            <MenuItem value="SHORT_ANSWER">Short Answer</MenuItem>
                            <MenuItem value="FILL_BLANK">Fill in the Blank</MenuItem>
                            <MenuItem value="NUMERIC">Numeric</MenuItem>
                            <MenuItem value="MULTIPLE_SELECT">Multiple Select</MenuItem>
                            <MenuItem value="ORDERING">Ordering</MenuItem>
                            <MenuItem value="MATCHING">Matching</MenuItem>
                            <MenuItem value="DRAG_DROP">Drag & Drop</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        All questions in this quiz will use this question type. Options will be randomized for students.
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={status}
                            label="Status"
                            onChange={(e) => setStatus(e.target.value as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED')}
                        >
                            <MenuItem value="DRAFT">Draft</MenuItem>
                            <MenuItem value="PUBLISHED">Published</MenuItem>
                            <MenuItem value="ARCHIVED">Archived</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Time Limit (Minutes)"
                        value={timeLimit}
                        onChange={(e) => setTimeLimit(parseInt(e.target.value))}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Pass Percentage"
                        value={passPercent}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        onChange={(e) => setPassPercent(parseInt(e.target.value))}
                    />
                </Grid>
            </Grid>

            {status === 'PUBLISHED' && questions.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Quiz must have at least one question before publishing.
                </Alert>
            )}

            {status === 'PUBLISHED' && !title.trim() && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Quiz must have a title before publishing.
                </Alert>
            )}

            {/* Questions Section */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Questions ({questions.length})</Typography>
                <Stack direction="row" spacing={2}>
                    <Button
                        startIcon={aiGenerating ? undefined : <AutoAwesomeIcon />}
                        variant="outlined"
                        color="secondary"
                        onClick={() => { setAiError(null); setAiDialogOpen(true); }}
                        disabled={aiGenerating}
                    >
                        {aiGenerating ? 'Generating…' : 'Generate with AI'}
                    </Button>
                    <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddQuestion}>
                        Add Question
                    </Button>
                </Stack>
            </Box>

            {/* AI Generate Dialog */}
            <Dialog open={aiDialogOpen} onClose={() => !aiGenerating && setAiDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon color="secondary" /> Generate Quiz Questions with AI
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                        {aiError && <Alert severity="error" onClose={() => setAiError(null)}>{aiError}</Alert>}
                        <Alert severity="info">
                            AI will generate questions for this topic. You can edit or add more after generation.
                            Supported types: Multiple Choice, True/False, Short Answer.
                        </Alert>
                        <TextField
                            label="Number of questions"
                            type="number"
                            value={aiQuestionCount}
                            onChange={(e) => setAiQuestionCount(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 5)))}
                            inputProps={{ min: 1, max: 20 }}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                                value={aiDifficulty}
                                label="Difficulty"
                                onChange={(e) => setAiDifficulty(e.target.value)}
                            >
                                <MenuItem value="LOW">Easy</MenuItem>
                                <MenuItem value="MEDIUM">Medium</MenuItem>
                                <MenuItem value="HIGH">Hard</MenuItem>
                            </Select>
                        </FormControl>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Question types to generate</Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {QUIZ_AI_QUESTION_TYPES.map(({ value, label }) => (
                                    <FormControlLabel
                                        key={value}
                                        control={
                                            <Checkbox
                                                checked={aiQuestionTypes.includes(value)}
                                                onChange={() => toggleAiQuestionType(value)}
                                            />
                                        }
                                        label={label}
                                    />
                                ))}
                            </Stack>
                        </Box>
                        <TextField
                            label="Additional instructions (optional)"
                            multiline
                            rows={2}
                            value={aiInstructions}
                            onChange={(e) => setAiInstructions(e.target.value)}
                            placeholder="e.g. Focus on definitions, include one calculation..."
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setAiDialogOpen(false)} disabled={aiGenerating}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleGenerateWithAI}
                        disabled={aiGenerating || aiQuestionTypes.length === 0}
                        startIcon={aiGenerating ? undefined : <AutoAwesomeIcon />}
                    >
                        {aiGenerating ? 'Generating…' : 'Generate questions'}
                    </Button>
                </DialogActions>
            </Dialog>

            {questions.map((q, index) => (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }} key={q.id}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <IconButton sx={{ cursor: 'grab' }}><DragIcon /></IconButton>
                        </Grid>
                        <Grid item xs>
                            <TextField
                                fullWidth
                                label={`Question ${index + 1}`}
                                value={q.content}
                                multiline
                                maxRows={2}
                                onChange={(e) => handleUpdateQuestion(index, 'content', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            <TextField
                                size="small"
                                type="number"
                                label="Points"
                                value={q.points || 1}
                                onChange={(e) => handleUpdateQuestion(index, 'points', parseInt(e.target.value) || 1)}
                                sx={{ width: 80 }}
                            />
                        </Grid>
                        <Grid item>
                            <IconButton color="error" onClick={() => handleDeleteQuestion(index)}>
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    {/* Question Type Specific Editors */}
                    <Box sx={{ mt: 2, ml: 6 }}>
                        {quizQuestionType === 'MCQ' && (
                            <>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Options (one per line)
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    value={Array.isArray(q.options) ? q.options.join('\n') : ''}
                                    onChange={(e) => {
                                        const opts = e.target.value.split('\n').filter(s => s.trim());
                                        handleUpdateQuestion(index, 'options', opts);
                                    }}
                                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                                />
                                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                    <InputLabel>Correct Answer</InputLabel>
                                    <Select
                                        value={q.correctAnswer || ''}
                                        label="Correct Answer"
                                        onChange={(e) => handleUpdateQuestion(index, 'correctAnswer', e.target.value)}
                                    >
                                        {Array.isArray(q.options) && q.options.map((opt: any, i: number) => (
                                            <MenuItem key={i} value={opt}>{opt}</MenuItem>
                                        )                                        )}
                                    </Select>
                                </FormControl>
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Options will be randomized for students when they take the quiz.
                                </Alert>
                            </>
                        )}

                        {quizQuestionType === 'TRUE_FALSE' && (
                            <FormControl fullWidth size="small">
                                <InputLabel>Correct Answer</InputLabel>
                                <Select
                                    value={q.correctAnswer || 'True'}
                                    label="Correct Answer"
                                    onChange={(e) => handleUpdateQuestion(index, 'correctAnswer', e.target.value)}
                                >
                                    <MenuItem value="True">True</MenuItem>
                                    <MenuItem value="False">False</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        {quizQuestionType === 'SHORT_ANSWER' && (
                            <TextField
                                fullWidth
                                size="small"
                                label="Correct Answer"
                                value={typeof q.correctAnswer === 'string' ? q.correctAnswer : ''}
                                onChange={(e) => handleUpdateQuestion(index, 'correctAnswer', e.target.value)}
                                helperText="Case-insensitive matching"
                            />
                        )}

                        {quizQuestionType === 'FILL_BLANK' && (
                            <>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    size="small"
                                    label="Question Text (use ___ for blanks)"
                                    value={q.content || ''}
                                    onChange={(e) => handleUpdateQuestion(index, 'content', e.target.value)}
                                    placeholder="The answer is ___."
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Answer for Blank 1"
                                    sx={{ mt: 1 }}
                                    value={typeof q.correctAnswer === 'object' && q.correctAnswer ? (q.correctAnswer as any).blank1 || '' : ''}
                                    onChange={(e) => {
                                        const ans = { blank1: e.target.value };
                                        handleUpdateQuestion(index, 'correctAnswer', ans);
                                    }}
                                />
                            </>
                        )}

                        {quizQuestionType === 'NUMERIC' && (
                            <>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Correct Answer (Number)"
                                    value={typeof q.correctAnswer === 'number' ? q.correctAnswer : ''}
                                    onChange={(e) => handleUpdateQuestion(index, 'correctAnswer', parseFloat(e.target.value) || 0)}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Tolerance (±)"
                                    sx={{ mt: 1 }}
                                    value={(Array.isArray(q.options) && q.options[0] && (q.options[0] as any).tolerance) || 0}
                                    onChange={(e) => {
                                        const opts = [{ tolerance: parseFloat(e.target.value) || 0 }];
                                        handleUpdateQuestion(index, 'options', opts);
                                    }}
                                />
                            </>
                        )}

                        {quizQuestionType === 'MULTIPLE_SELECT' && (
                            <>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Options (one per line)
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    value={Array.isArray(q.options) ? q.options.join('\n') : ''}
                                    onChange={(e) => {
                                        const opts = e.target.value.split('\n').filter(s => s.trim());
                                        handleUpdateQuestion(index, 'options', opts);
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Select all correct answers:
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Array.isArray(q.options) && q.options.map((opt: any, i: number) => {
                                        const selected = Array.isArray(q.correctAnswer) && q.correctAnswer.includes(opt);
                                        return (
                                            <Chip
                                                key={i}
                                                label={opt}
                                                color={selected ? 'primary' : 'default'}
                                                onClick={() => {
                                                    const current = Array.isArray(q.correctAnswer) ? [...q.correctAnswer] : [];
                                                    const newAnswer = selected
                                                        ? current.filter((a: any) => a !== opt)
                                                        : [...current, opt];
                                                    handleUpdateQuestion(index, 'correctAnswer', newAnswer);
                                                }}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        );
                                    })}
                                </Box>
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Options will be randomized for students when they take the quiz.
                                </Alert>
                            </>
                        )}

                        {quizQuestionType === 'ORDERING' && (
                            <>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Items to order (one per line, in correct order)
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    value={Array.isArray(q.options) ? q.options.join('\n') : ''}
                                    onChange={(e) => {
                                        const opts = e.target.value.split('\n').filter(s => s.trim());
                                        handleUpdateQuestion(index, 'options', opts);
                                        handleUpdateQuestion(index, 'correctAnswer', opts); // Same as correct order
                                    }}
                                    placeholder="First&#10;Second&#10;Third"
                                />
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    The order you enter is the correct answer. Options will be randomized for students, who will drag to reorder.
                                </Alert>
                            </>
                        )}

                        {quizQuestionType === 'MATCHING' && (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Left Items (one per line)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={5}
                                            size="small"
                                            value={q.options && typeof q.options === 'object' && (q.options as any).leftItems
                                                ? (q.options as any).leftItems.join('\n') : ''}
                                            onChange={(e) => {
                                                const leftItems = e.target.value.split('\n').filter(s => s.trim());
                                                const rightItems = (q.options && typeof q.options === 'object' && (q.options as any).rightItems) || [];
                                                handleUpdateQuestion(index, 'options', { leftItems, rightItems });
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Right Items (one per line, match in order)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={5}
                                            size="small"
                                            value={q.options && typeof q.options === 'object' && (q.options as any).rightItems
                                                ? (q.options as any).rightItems.join('\n') : ''}
                                            onChange={(e) => {
                                                const rightItems = e.target.value.split('\n').filter(s => s.trim());
                                                const leftItems = (q.options && typeof q.options === 'object' && (q.options as any).leftItems) || [];
                                                handleUpdateQuestion(index, 'options', { leftItems, rightItems });
                                                // Auto-set correct answer mapping
                                                const mapping: any = {};
                                                leftItems.forEach((left: string, i: number) => {
                                                    if (rightItems[i]) mapping[left] = rightItems[i];
                                                });
                                                handleUpdateQuestion(index, 'correctAnswer', mapping);
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Match items in the same order (Left Item 1 → Right Item 1, etc.). Right-side items will be randomized for students, who will drag to match.
                                </Alert>
                            </>
                        )}

                        {quizQuestionType === 'DRAG_DROP' && (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Drag Items (one per line)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            size="small"
                                            value={q.options && typeof q.options === 'object' && (q.options as any).items
                                                ? (q.options as any).items.join('\n') : ''}
                                            onChange={(e) => {
                                                const items = e.target.value.split('\n').filter(s => s.trim());
                                                const zones = (q.options && typeof q.options === 'object' && (q.options as any).zones) || [];
                                                handleUpdateQuestion(index, 'options', { items, zones });
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Drop Zones (one per line, match in order)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            size="small"
                                            value={q.options && typeof q.options === 'object' && (q.options as any).zones
                                                ? (q.options as any).zones.join('\n') : ''}
                                            onChange={(e) => {
                                                const zones = e.target.value.split('\n').filter(s => s.trim());
                                                const items = (q.options && typeof q.options === 'object' && (q.options as any).items) || [];
                                                handleUpdateQuestion(index, 'options', { items, zones });
                                                // Auto-set correct answer mapping
                                                const mapping: any = {};
                                                items.forEach((item: string, i: number) => {
                                                    if (zones[i]) mapping[item] = zones[i];
                                                });
                                                handleUpdateQuestion(index, 'correctAnswer', mapping);
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Match items in the same order (Item 1 → Zone 1, etc.). Items and zones will be randomized for students, who will drag items into zones.
                                </Alert>
                            </>
                        )}

                        {/* Explanation field for all types */}
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            label="Explanation (shown after submission)"
                            sx={{ mt: 2 }}
                            value={q.explanation || ''}
                            onChange={(e) => handleUpdateQuestion(index, 'explanation', e.target.value)}
                            placeholder="Explain why this answer is correct..."
                        />
                    </Box>
                </Paper>
            ))}

            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                    Save Quiz
                </Button>
            </Box>
        </Paper>
    );
};

export default QuizEditor;
